/**
 * injectShowButton - Inyecta configuración de visibilidad del botón flotante del chat
 *
 * Este módulo permite controlar si se muestra el botón flotante del chat widget
 * a través de la configuración global HELLOMATIK_CONFIG. Prioriza la configuración
 * de la base de datos sobre el parámetro data-show-button del script.
 *
 * @module injectShowButton
 */

/**
 * Inyecta la configuración de visibilidad del botón flotante
 *
 * Lee la configuración de show-button desde HELLOMATIK_CONFIG y actualiza
 * la variable global HELLOMATIK_SHOW_BUTTON. Si existe en la configuración,
 * sobrescribe el valor pasado por el atributo data-show-button del script.
 *
 * Prioridad:
 * 1. Config de base de datos (show-button en tabla config)
 * 2. Atributo data-show-button del script (valor por defecto)
 *
 * @example
 * // En la configuración del core:
 * {
 *   "config_id": "show-button",
 *   "config_value": "true"
 * }
 *
 * // En el loader, después de cargar la configuración:
 * injectShowButton();
 *
 * @returns {void}
 */
export function injectShowButton(): void {
  try {
    // Obtener la configuración desde la variable global
    const config = ( window as any ).HELLOMATIK_CONFIG;

    if( !config )
      return;

    // Obtener la configuración de show-button
    const showButton = config['show-button'];

    // Solo sobrescribir si existe en la configuración
    // Aceptamos string 'true'/'false' o boolean true/false
    if( showButton !== undefined && showButton !== null )
    {
      // Normalizar a string 'true' o 'false' para consistencia
      let normalizedValue: string;
      
      if( typeof showButton === 'boolean' )
        normalizedValue = showButton ? 'true' : 'false';
      else if( typeof showButton === 'number' )
        normalizedValue = showButton !== 0 ? 'true' : 'false';
      else if( typeof showButton === 'string' )
      {
        const normalized = showButton.trim().toLowerCase();
        // Si viene vacío desde config, no sobrescribir el valor original del script.
        if( normalized === '' )
          return;
        normalizedValue = ['true', '1', 'yes', 'on', 'si'].includes( normalized ) ? 'true' : 'false';
      }
      else
        return; // Tipo no válido, no sobrescribir

      // Sobrescribir la variable global establecida por el loader desde el script
      ( window as any ).HELLOMATIK_SHOW_BUTTON = normalizedValue;
    }

  } catch( error ) {
    if( import.meta.env.DEV ) console.error( '[Show Button] Failed to inject show button configuration:', error );
  }
}

/**
 * Resetea la configuración de show-button al valor por defecto del script
 * Útil para pruebas o cuando se quiere volver a la configuración original
 *
 * @param originalValue El valor original del atributo data-show-button del script
 * @returns {void}
 */
export function resetShowButton( originalValue: string = 'true' ): void {
  ( window as any ).HELLOMATIK_SHOW_BUTTON = originalValue;
}
